import { ColumnDef } from "@tanstack/react-table";
import { RightArrowIcon } from "assets/icons";
import cn from "classnames";
import { useLanguage } from "hooks";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { Icon, ItemsTable, PersonInfo } from "ui";

import { ResellerReferralsOrderBy } from "../../constants";
import { ResellerReferral } from "../../resellers.types";
import { ResellerLevelLabel } from "../ResellerLevelLabel";
import { ResellerReferralsTableProps } from "./ResellerReferralsTable.types";

import styles from "./ResellerReferralsTable.module.scss";

const ResellerReferralsTable = ({
  referrals,
  isLoading,
  sorting,
  setSorting,
  fallbackCode,
  onRowClick
}: ResellerReferralsTableProps) => {
  const { currentLanguage } = useLanguage();

  const columns: Array<ColumnDef<ResellerReferral, ResellerReferral[]>> =
    useMemo(
      () => [
        {
          id: "referee",
          header: t("Referee"),
          size: 24,
          minSize: 24,
          cell: ({ row }) => (
            <PersonInfo
              image={
                row.original.imageUrl ? { url: row.original.imageUrl } : null
              }
              dimensions={{ width: 36, height: 36, borderRadius: "100%" }}
              personName={`${row.original.firstname} ${row.original.lastname}`}
              nameClassName={styles.person_name}
              bottomText={row.original.phoneNumber}
              bottomTextClassName={styles.person_phone}
            />
          )
        },
        {
          id: "level",
          header: t("Level"),
          size: 14,
          minSize: 14,
          cell: ({ row }) => <ResellerLevelLabel level={row.original.level} />
        },
        {
          id: ResellerReferralsOrderBy.SignedUpAt,
          accessorKey: "signedUpAt",
          header: t("Signed up"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => (
            <div className={styles.signedUpCell}>
              <span className={styles.signedUpCell_date}>
                {moment(row.original.signedUpAt).format("DD/MM/YYYY")}
              </span>
              <span className={styles.signedUpCell_code}>
                {/* The code was only recorded from this release on, so older
                    links fall back to the influencer's own code. */}
                {row.original.codeUsed ?? fallbackCode ?? "—"}
              </span>
            </div>
          )
        },
        {
          id: ResellerReferralsOrderBy.OrderCount,
          accessorKey: "orderCount",
          header: t("Orders"),
          size: 9,
          minSize: 9,
          cell: ({ row }) => row.original.orderCount
        },
        {
          id: ResellerReferralsOrderBy.Revenue,
          accessorKey: "revenue",
          header: `${t("Revenue")} (FCFA)`,
          size: 14,
          minSize: 14,
          cell: ({ row }) => (
            <span
              className={cn(styles.amountCell, {
                [styles.amountCell__muted]: !row.original.revenue
              })}
            >
              {row.original.revenue.toLocaleString("en-US")}
            </span>
          )
        },
        {
          id: ResellerReferralsOrderBy.Commission,
          accessorKey: "commission",
          header: `${t("Commission")} (FCFA)`,
          size: 14,
          minSize: 14,
          cell: ({ row }) => (
            <span
              className={cn(styles.amountCell, {
                [styles.amountCell__commission]: !!row.original.commission,
                [styles.amountCell__muted]: !row.original.commission
              })}
            >
              {row.original.commission.toLocaleString("en-US")}
            </span>
          )
        },
        {
          id: "lastOrderAt",
          header: t("Last order"),
          size: 8,
          minSize: 8,
          cell: ({ row }) =>
            row.original.lastOrderAt
              ? moment(row.original.lastOrderAt).format("DD/MM/YYYY")
              : "—"
        },
        {
          id: "chevron",
          header: () => null,
          size: 2,
          minSize: 2,
          cell: () => (
            <div className={styles.chevron}>
              <Icon as={RightArrowIcon} width={18} height={18} />
            </div>
          )
        }
      ],
      [referrals, fallbackCode, currentLanguage]
    );

  return (
    <ItemsTable
      className={styles.table}
      columns={columns}
      data={referrals}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={65}
      onRowClick={onRowClick}
      emptyListMessage="No referee yet"
    />
  );
};

export default ResellerReferralsTable;
